// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ResizeMode, Video, type AVPlaybackStatus } from 'expo-av';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { VideoScrubBar, useVideoScrub } from '@/components/VideoScrubBar';
import { HoleMapCanvas, type MapOverlayPolygon } from '@/features/hole/holeview/HoleMapCanvas';
import { OVERLAY_COLORS } from '@/features/hole/holeview/holeShapes';
import { buildTileUrl, computeLayoutAt } from '@/features/hole/holeview/tileMath';
import { getSatelliteTileUrlTemplate } from '@/features/hole/holeview/tileSource';
import { formatHoleDistance } from '@/features/units/distance';
import { useI18n } from '@/i18n';
import { useDistanceUnit } from '@/stores/distanceUnitState';
import appColors, { alpha } from '@/theme/colors';

import { buildHoleCameraPath } from './flyoverCamera';
import { readFlyoverDistance } from './flyoverDistanceReading';
import type { FlyoverHole } from './useCourseFlyover';
import type { FlyoverVideo } from './useFlyoverVideos';

export type FlyoverPlayerProps = {
  courseName: string;
  /** Holes worth flying, course order — `useCourseFlyover`'s output. */
  holes: FlyoverHole[];
  loading: boolean;
  unavailable: boolean;
  onClose: () => void;
  /**
   * Server-rendered flyovers per hole, where the offline render script has produced them.
   * A hole with a video plays it; every other hole plays the live 2D flyover. The video
   * carries the flight's distance track too, which is what the scrub bar reads out.
   */
  videoByHole?: Record<number, FlyoverVideo>;
};

/** Roughly the size the canvas renders at; only used to list tiles worth prefetching. */
const PREFETCH_VIEWPORT = { width: 400, height: 400 };

/**
 * Plays a course hole by hole: the camera lifts off the tee, glides down the line of play
 * and settles on the green, then moves to the next hole.
 *
 * The animation drives `HoleMapCanvas` through its `camera` prop — one
 * `requestAnimationFrame` loop advancing a clock, the camera path turning that clock into
 * a centre. The span and bearing are constant per hole, so the tile zoom level never
 * changes mid-flight and panning reuses the mounted tiles.
 */
export function FlyoverPlayer({
  courseName,
  holes,
  loading,
  unavailable,
  onClose,
  videoByHole,
}: FlyoverPlayerProps) {
  const { t } = useI18n();
  const distanceUnit = useDistanceUnit();
  const [holeIndex, setHoleIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [clockMs, setClockMs] = useState(0);

  const hole = holes[holeIndex] ?? null;
  const video = hole ? (videoByHole?.[hole.holeNumber] ?? null) : null;
  const videoUrl = video?.url ?? null;
  const path = useMemo(
    () => (hole && hole.green ? buildHoleCameraPath({ tee: hole.tees[0], green: hole.green }) : null),
    [hole]
  );

  // The clock only ever moves relative to when play (re)started, so pausing holds the
  // frame and resuming continues from it rather than snapping back to the tee.
  const playedUpToRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  // Where the stored video is, for the scrub bar.
  const videoRef = useRef<Video>(null);
  // Taking the bar stops the picture, so the show stops with it: the button has to offer
  // play rather than pause, and on a hole with no video the 2D flight must not glide on
  // under the finger either.
  const scrub = useVideoScrub(videoRef, { onScrubStart: () => setPlaying(false) });
  const resetScrub = scrub.reset;

  useEffect(() => {
    playedUpToRef.current = 0;
    setClockMs(0);
    resetScrub();
  }, [holeIndex, holes, resetScrub]);

  const advance = useCallback(() => {
    setHoleIndex((index) => {
      if (index + 1 < holes.length) return index + 1;
      // The last green is the end of the show; hold there rather than looping.
      setPlaying(false);
      return index;
    });
  }, [holes.length]);

  useEffect(() => {
    // A stored video drives its own clock; the animation loop only runs the 2D flight.
    if (!playing || !path || videoUrl) return undefined;

    const startedAt = Date.now() - playedUpToRef.current;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      playedUpToRef.current = elapsed;
      if (elapsed >= path.totalMs) {
        playedUpToRef.current = 0;
        advance();
        return;
      }
      setClockMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [advance, path, playing, videoUrl]);

  const camera = useMemo(() => (path ? path.cameraAt(clockMs) : null), [clockMs, path]);

  const polygons = useMemo<MapOverlayPolygon[]>(
    () =>
      (hole?.shapes ?? []).map((shape) => ({
        id: shape.id,
        ring: shape.ring,
        color: OVERLAY_COLORS[shape.kind] ?? appColors.text.onMedia,
      })),
    [hole]
  );

  // Warms the next hole's tiles while this hole plays, so the cut is not a blank mosaic.
  // Web leaves it to the browser's own cache; Image.prefetch is a native affair.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const next = holes[holeIndex + 1];
    if (!next?.green) return;
    const nextPath = buildHoleCameraPath({ tee: next.tees[0], green: next.green });
    const template = getSatelliteTileUrlTemplate();
    for (const at of [0, nextPath.durationMs / 2, nextPath.durationMs]) {
      const layout = computeLayoutAt({
        center: nextPath.cameraAt(at).center,
        spanMeters: nextPath.cameraAt(at).spanMeters,
        bearingDegrees: nextPath.cameraAt(at).bearingDegrees,
        ...PREFETCH_VIEWPORT,
      });
      for (const tile of layout?.tiles ?? []) {
        // Best-effort warm-up; the RN test renderer's Image stub returns no promise at all.
        void Image.prefetch(buildTileUrl(template, tile))?.catch?.(() => {});
      }
    }
  }, [holeIndex, holes]);

  const stepTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= holes.length) return;
      setHoleIndex(index);
      setPlaying(true);
    },
    [holes.length]
  );

  const caption = useMemo(() => {
    if (!hole) return '';
    const pieces = [t('tourPreview.holeCaption', { number: hole.holeNumber })];
    if (hole.par !== null) pieces.push(t('tourPreview.parShort', { par: hole.par }));
    if (hole.strokeIndex !== null) {
      pieces.push(t('tourPreview.strokeIndexShort', { strokeIndex: hole.strokeIndex }));
    }
    if (hole.lengthMeters !== null && hole.lengthMeters > 0) {
      pieces.push(formatHoleDistance(hole.lengthMeters, distanceUnit));
    }
    return pieces.join(' · ');
  }, [distanceUnit, hole, t]);

  /**
   * The second line: where in the round this hole is, and what is mapped around it. The
   * shapes are the ones the flight already draws, so this counts what is on screen rather
   * than claiming anything the map does not show — a hole whose bunkers have not been
   * mapped says nothing about bunkers instead of saying there are none.
   */
  const detail = useMemo(() => {
    if (!hole) return '';
    const pieces = [
      t('tourPreview.holeOfTotal', { position: holeIndex + 1, total: holes.length }),
    ];
    const bunkers = hole.shapes.filter((shape) => shape.kind === 'bunker').length;
    if (bunkers > 0) pieces.push(t('tourPreview.bunkerCount', { count: bunkers }));
    if (hole.shapes.some((shape) => shape.kind === 'water')) pieces.push(t('tourPreview.water'));
    if (hole.tees.length > 1) pieces.push(t('tourPreview.teeCount', { count: hole.tees.length }));
    return pieces.join(' · ');
  }, [hole, holeIndex, holes.length, t]);

  /**
   * How far the camera is along the hole, above the scrub thumb — the same reading the hole
   * view gives, off the same track the renderer published. A 2D flight has no track to read
   * and a course rendered before the track existed carries none, so both show no distance.
   */
  const flightDistance = useMemo(
    () =>
      video?.distances
        ? readFlyoverDistance(video.distances, scrub.progress, hole?.par ?? null)
        : null,
    [hole?.par, scrub.progress, video]
  );

  return (
    <View style={styles.container} testID="flyover-player">
      <View style={styles.header}>
        <Text style={styles.title} numberOfLines={1}>
          {courseName}
        </Text>
        <Pressable
          accessibilityRole="button"
          testID="flyover-close"
          onPress={onClose}
          style={styles.closeButton}
        >
          <Text style={styles.closeText}>✕</Text>
        </Pressable>
      </View>

      {loading ? (
        <View style={styles.stateBox} testID="flyover-loading">
          <ActivityIndicator color={appColors.accent.primary} />
        </View>
      ) : unavailable || !hole || !camera ? (
        <View style={styles.stateBox} testID="flyover-unavailable">
          <Text style={styles.stateText}>{t('tourPreview.courseNotMapped')}</Text>
        </View>
      ) : (
        <>
          {videoUrl ? (
            <>
              <Video
                testID="flyover-video"
                ref={videoRef}
                source={{ uri: videoUrl }}
                style={styles.video}
                // Same defect as the hole view's player: an absolutely positioned <video>
                // with auto width takes its own pixel size, not the box's, so on web the box
                // only crops it. Filling the box is what makes CONTAIN fit the picture.
                videoStyle={styles.videoSurface}
                resizeMode={ResizeMode.CONTAIN}
                shouldPlay={playing}
                // The default half-second between status frames is visible as a stepping
                // progress bar; this is the rate the fill moves at.
                progressUpdateIntervalMillis={100}
                onPlaybackStatusUpdate={(status: AVPlaybackStatus) => {
                  scrub.applyStatus(status);
                  if (status.isLoaded && status.didJustFinish) advance();
                }}
              />
              <VideoScrubBar
                scrub={scrub}
                accessibilityLabel={t('tourPreview.videoPosition')}
                testIDPrefix="flyover"
                thumbLabel={
                  flightDistance
                    ? formatHoleDistance(flightDistance.meters, distanceUnit)
                    : null
                }
              />
            </>
          ) : (
            <HoleMapCanvas
              tees={hole.tees}
              green={hole.green}
              loading={false}
              unavailable={false}
              polygons={polygons}
              camera={camera}
            />
          )}
          <Text style={styles.caption} testID="flyover-caption">
            {caption}
          </Text>
          {detail ? (
            <Text style={styles.detail} testID="flyover-detail">
              {detail}
            </Text>
          ) : null}
          <View style={styles.controls}>
            <Pressable
              accessibilityRole="button"
              testID="flyover-previous"
              disabled={holeIndex === 0}
              onPress={() => stepTo(holeIndex - 1)}
              style={[styles.controlButton, holeIndex === 0 ? styles.controlDisabled : null]}
            >
              <Text style={styles.controlText}>{t('tourPreview.previousHole')}</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-play-pause"
              onPress={() => setPlaying((value) => !value)}
              style={styles.controlButton}
            >
              <Text style={styles.controlText}>
                {playing ? t('tourPreview.pause') : t('tourPreview.play')}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              testID="flyover-next"
              disabled={holeIndex >= holes.length - 1}
              onPress={() => stepTo(holeIndex + 1)}
              style={[
                styles.controlButton,
                holeIndex >= holes.length - 1 ? styles.controlDisabled : null,
              ]}
            >
              <Text style={styles.controlText}>{t('tourPreview.nextHole')}</Text>
            </Pressable>
          </View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    padding: 12,
    gap: 10,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  // Panel chrome, so it takes the skin's ink rather than white: on a light skin — Yardage
  // Book's paper — white text on the panel is invisible.
  title: { color: appColors.text.heading, fontSize: 16, fontWeight: '700', flexShrink: 1 },
  closeButton: { padding: 6 },
  closeText: { color: appColors.text.primary, fontSize: 16 },
  stateBox: {
    height: 220,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stateText: { color: appColors.text.primary, fontSize: 14, textAlign: 'center' },
  video: { width: '100%', aspectRatio: 16 / 9, borderRadius: 10, backgroundColor: appColors.neutral.black },
  videoSurface: { width: '100%', height: '100%' },
  caption: {
    color: appColors.text.heading,
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  detail: {
    color: appColors.text.muted,
    fontSize: 12,
    textAlign: 'center',
    marginTop: -4,
    fontVariant: ['tabular-nums'],
  },
  controls: { flexDirection: 'row', justifyContent: 'center', gap: 10 },
  controlButton: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: alpha(appColors.text.primary, 0.12),
  },
  controlDisabled: { opacity: 0.4 },
  controlText: { color: appColors.text.primary, fontSize: 13, fontWeight: '600' },
});
